/**
 * Horas de equipo de un APU — UNA sola base: horas POR UNIDAD del concepto.
 *
 * EL BUG QUE CIERRA (reportado por Sebastián, 2026-08-11): en la vista del
 * análisis de precio, una grúa torre mostraba 6 horas × 124 el costo horario y un
 * importe de 93. 6 × 124 = 744, no 93. El renglón no cerraba en pantalla.
 *
 * El motivo era que el número y el importe estaban en bases distintas: el campo
 * se llamaba `tiempo_uso_hr_por_jornada` (horas por JORNADA) y el sistema lo
 * dividía por el rendimiento para llevarlo a costo POR UNIDAD, que es la base de
 * todo lo demás del APU. La vista mostraba el número sin convertir al lado del
 * importe ya convertido.
 *
 * Peor todavía: el prompt NUNCA le explicaba al modelo qué significaba ese campo
 * — la única definición que veía era la línea `- tiempo_uso_hr_por_jornada
 * (number)`. Y en el mismo prompt la mano de obra se pedía POR UNIDAD
 * (`horas_cuadrilla_por_unidad`). Dos bases, ninguna explicada. Si el modelo
 * entendía "horas para hacer una unidad", el sistema dividía por el rendimiento
 * sin motivo y el equipo quedaba subvaluado por ese factor.
 *
 * LA DECISIÓN: un análisis de PRECIO UNITARIO está expresado por unidad de obra,
 * de punta a punta. El equipo se pide igual que el resto —horas por unidad del
 * concepto— y el sistema no divide por nada. Si a una máquina le toca estar en
 * obra toda la jornada independientemente de la producción (una grúa torre, un
 * montacargas), pasar sus horas de jornada a horas por unidad es parte del
 * análisis y lo hace el modelo al contestar.
 *
 * Esta función es el ÚNICO lugar donde se interpreta la cantidad de un renglón de
 * equipo. La comparten el worker (costeo), el API (export a m1) y la web (vista),
 * para que el número que se muestra sea exactamente el que se costea.
 */

/** Las tres formas en las que un renglón de equipo puede traer su cantidad. */
export interface EquipoCantidadLike {
  /** Forma vigente: horas de la máquina por UNIDAD del concepto. */
  horas_uso_por_unidad?: number | null;
  /** LEGACY: horas por JORNADA (APU generados antes del 2026-08-11). */
  tiempo_uso_hr_por_jornada?: number | null;
  /** Forma catálogo/biblioteca: cantidad ya por unidad del concepto. */
  cantidad?: number | null;
}

export interface CantidadEquipoPorUnidad {
  /**
   * Cantidad por UNIDAD del concepto. Es la que multiplica al precio unitario:
   * `cantidad × precio = importe`. Se muestra y se costea con este mismo número.
   */
  cantidad: number;
  /**
   * true = el renglón venía en la base vieja (por jornada) y hubo que dividirlo
   * por el rendimiento. Sirve para distinguir en el dato un APU heredado de uno
   * nuevo, sin inventar una declaración que el modelo nunca hizo.
   */
  heredado: boolean;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Cantidad de un renglón de equipo llevada a POR UNIDAD del concepto.
 *
 * @param rendAjustado rendimiento ajustado (unidades por jornada). Solo se usa
 *   para los renglones heredados; con 0 o negativo esos dan 0 (es lo que hacía
 *   el costeo antes: sin rendimiento no hay cómo prorratear una jornada).
 */
export function cantidadEquipoPorUnidad(
  e: EquipoCantidadLike,
  rendAjustado: number,
): CantidadEquipoPorUnidad {
  // Forma vigente: el número YA viene por unidad. No se divide por nada.
  if (e.horas_uso_por_unidad != null) {
    return { cantidad: num(e.horas_uso_por_unidad), heredado: false };
  }
  // LEGACY: para estos APU el campo SÍ significaba horas por jornada, así que se
  // sigue prorrateando por el rendimiento. Es compatibilidad con datos que ya
  // están guardados, no una base alternativa que se pueda elegir.
  if (e.tiempo_uso_hr_por_jornada != null) {
    const horas = num(e.tiempo_uso_hr_por_jornada);
    return { cantidad: rendAjustado > 0 ? horas / rendAjustado : 0, heredado: true };
  }
  // Forma catálogo/biblioteca: `cantidad` ya está por unidad del concepto.
  return { cantidad: num(e.cantidad), heredado: false };
}
